import { Component, computed, inject } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { CustomerSubscription, ProfileService } from '@tfs/auth';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

@Component({
  selector: 'tfs-subscription-status',
  imports: [MatProgressBarModule],
  templateUrl: './subscription-status.component.html',
  styleUrl: './subscription-status.component.scss',
})
export class SubscriptionStatusComponent {
  private readonly profileService = inject(ProfileService);

  protected readonly subscription = this.profileService.subscription;

  protected readonly isTrial = computed(() => this.subscription()?.status === 'Trial');

  protected readonly isActive = computed(() => this.subscription()?.status === 'Active');

  protected readonly shouldDisplay = computed(() => {
    const subscription = this.subscription();
    return Boolean(subscription && (this.isTrial() || this.isActive()));
  });

  protected readonly daysRemaining = computed(() => {
    const subscription = this.subscription();
    if (!subscription) return 0;

    const targetDate = this.getTargetDate(subscription);
    if (!targetDate) return 0;

    return this.calculateDaysUntil(targetDate);
  });

  protected readonly progressPercentage = computed(() => {
    const subscription = this.subscription();
    if (!subscription?.startDate) return 0;

    const targetDate = this.getTargetDate(subscription);
    if (!targetDate) return 0;

    return this.calculateProgress(new Date(subscription.startDate), new Date(targetDate));
  });

  protected readonly displayText = computed(() =>
    this.isTrial() ? 'Trial version ends in' : 'Next billing cycle'
  );

  /**
   * Determines the target date based on subscription type
   */
  private getTargetDate(subscription: CustomerSubscription): string | null | undefined {
    return this.isTrial() ? subscription.endDate : subscription.nextBilling;
  }

  /**
   * Calculates days remaining until target date
   */
  private calculateDaysUntil(targetDate: string): number {
    const now = Date.now();
    const target = new Date(targetDate).getTime();
    const diffDays = Math.ceil((target - now) / MS_PER_DAY);
    return Math.max(diffDays, 0);
  }

  /**
   * Calculates progress percentage between start and end dates
   */
  private calculateProgress(startDate: Date, endDate: Date): number {
    const now = Date.now();
    const start = startDate.getTime();
    const end = endDate.getTime();

    if (now >= end) return 100;
    if (now <= start) return 0;

    const total = end - start;
    const elapsed = now - start;
    return Math.min(Math.round((elapsed / total) * 100), 100);
  }
}
